import { useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Activity } from "lucide-react";
import { toast } from "sonner";

const ICON = {
  pass: { I: CheckCircle2, c: "text-[hsl(var(--success))]" },
  warn: { I: AlertTriangle, c: "text-[#B7791F]" },
  fail: { I: XCircle, c: "text-accent" },
};

export function EmbedHealth({ endpoint, tenantId, compact }) {
  const [hostUrl, setHostUrl] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setRes(null);
    try {
      const { data } = await opApi.post(endpoint, { tenant_id: tenantId, host_url: hostUrl || undefined });
      setRes(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Health check failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className={compact ? "" : "max-w-2xl"}>
      <div className="flex flex-col sm:flex-row gap-2 items-end">
        <div className="flex-1 w-full">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Host site URL (optional)</Label>
          <Input className="rounded-none mt-1" placeholder="https://your-clients-website.com/order" value={hostUrl}
            onChange={(e) => setHostUrl(e.target.value)} data-testid="health-host-input" />
        </div>
        <Button onClick={run} disabled={loading || (endpoint.includes("admin") && !tenantId)} className="rounded-none gap-2 h-10" data-testid="health-run-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />} Run check
        </Button>
      </div>

      {res && (
        <div className="mt-4 border border-border" data-testid="health-results">
          <div className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b border-border ${res.overall === "pass" ? "bg-[hsl(var(--success))]/10" : res.overall === "warn" ? "bg-[#B7791F]/10" : "bg-accent/10"}`}>
            {(() => { const { I, c } = ICON[res.overall]; return <I className={`h-4 w-4 ${c}`} />; })()}
            <span data-testid="health-overall">Overall: {res.overall.toUpperCase()}</span>
          </div>
          <div className="divide-y divide-border">
            {res.checks.map((c) => {
              const { I, cc } = ICON[c.status] ? { I: ICON[c.status].I, cc: ICON[c.status].c } : { I: AlertTriangle, cc: "" };
              return (
                <div key={c.id} className="flex items-start gap-3 px-4 py-3" data-testid={`health-check-${c.id}`}>
                  <I className={`h-4 w-4 mt-0.5 shrink-0 ${ICON[c.status]?.c || ""}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground break-words">{c.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
